import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Box, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { AddressFieldActionControl, AddressFieldEditorDialog, addressValueInputSx } from "../../../../../common/Addresses/AddressFields";
import { OptionItems, OverrideStatus } from "../../components/primitives";
import { COUNTRY_OPTIONS } from "../../constants/options";
import { ADDRESS_LINE_DEFINITIONS } from "../../model/addressModel";
import { differsFromOrganization, isAddressFieldSettingOverride } from "../../model/overrideModel";
import { ui } from "../../styles";

const dropPosition = event => { const rect = event.currentTarget.getBoundingClientRect(); return event.clientY < rect.top + rect.height / 2 ? "before" : "after"; };

const DraggableAddressLines = ({
  address,
  addressSettings,
  addressType,
  masterAddress,
  masterAddressSettings,
  moveAddressLine,
  setAddressFieldSetting,
  setAddressFieldValue,
  showHidden,
  updateAddress
}) => {

  const intl = useIntl(),
    t = id => intl.formatMessage({ id }),
    [draggedKey, setDraggedKey] = useState(null),
    [dropTarget, setDropTarget] = useState(null),
    [editingFieldKey, setEditingFieldKey] = useState(null);

  const orderedFieldKeys = Object.entries(addressSettings || {})
    .sort(
      ([, a], [, b]) =>
        Number(a?.displayOrder ?? 0) -
        Number(b?.displayOrder ?? 0)
    )
    .map(([fieldKey]) => fieldKey);

  const visibleKeys = orderedFieldKeys.filter(
    fieldKey =>
      showHidden ||
      addressSettings[fieldKey]?.type !== "HIDDEN"
  );

  const resetDrag = () => { setDraggedKey(null); setDropTarget(null); };

  const resetSetting = (fieldKey, definition, masterSetting) => {
    setAddressFieldSetting(addressType, fieldKey, "type", masterSetting.type);
    setAddressFieldSetting(addressType, fieldKey, "label", masterSetting.label || definition.label);
  };

  const displayLabel = (fieldKey, definition) => {
    const setting = addressSettings?.[fieldKey];

    return setting?.label && setting.label !== definition.label
      ? setting.label
      : t(definition.messageId);
  };

  const renderValueField = (fieldKey, definition) => {
    const masterValue = masterAddress?.[fieldKey];
    const setting = addressSettings?.[fieldKey];
    const changed = differsFromOrganization(
      address?.[fieldKey],
      masterValue
    );

    const props = {
      fullWidth: true, label: displayLabel(fieldKey, definition), placeholder: setting.placeholder || "", value: address[fieldKey] || "",
      onChange: updateAddress(addressType, fieldKey), required: setting.type === "REQUIRED", sx: addressValueInputSx,
      helperText: <OverrideStatus changed={changed} masterValue={masterValue} onReset={changed ? () => setAddressFieldValue(addressType, fieldKey, masterValue ?? "") : null} compact />,
      FormHelperTextProps: { component: "div", sx: { minHeight: changed ? 15 : 0 } }
    };
    return <TextField {...props} />;
  };

  return <Stack spacing={.38}>
    {visibleKeys.map(fieldKey => {
      const definition = ADDRESS_LINE_DEFINITIONS[fieldKey]; if (!definition) return null;

      const setting = addressSettings?.[fieldKey];
      const masterSetting = masterAddressSettings?.[fieldKey];

      const settingChanged = isAddressFieldSettingOverride(address, masterAddress, fieldKey), hidden = setting.type === "HIDDEN";
      const showBefore = dropTarget?.fieldKey === fieldKey && dropTarget?.position === "before" && draggedKey !== fieldKey;
      const showAfter = dropTarget?.fieldKey === fieldKey && dropTarget?.position === "after" && draggedKey !== fieldKey;
      return <Stack key={fieldKey} direction="row" spacing={.38} alignItems="flex-start" draggable
        onDragStart={event => { setDraggedKey(fieldKey); setDropTarget(null); event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", fieldKey); }}
        onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; setDropTarget(fieldKey === draggedKey ? null : { fieldKey, position: dropPosition(event) }); }}
        onDrop={event => { event.preventDefault(); const sourceKey = draggedKey || event.dataTransfer.getData("text/plain"); const position = dropTarget?.fieldKey === fieldKey ? dropTarget.position : dropPosition(event); moveAddressLine(addressType, sourceKey, fieldKey, position); resetDrag(); }}
        onDragEnd={resetDrag}
        sx={{
          position: "relative", minHeight: 36, p: .2, ml: -.2, borderRadius: .9, opacity: draggedKey === fieldKey ? .48 : hidden ? .72 : 1,
          bgcolor: draggedKey === fieldKey ? ui.soft : hidden ? "#fafafa" : "transparent", transition: "opacity 100ms ease, background-color 120ms ease"
        }}>
        {(showBefore || showAfter) && <Box aria-hidden="true" sx={{
          position: "absolute", zIndex: 4, left: 25, right: 2, [showBefore ? "top" : "bottom"]: -4, height: "2px", borderRadius: 2,
          bgcolor: "primary.main", pointerEvents: "none", "&::before": { content: '""', position: "absolute", left: -3, top: -2, width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }
        }} />}
        <Tooltip title={t("admin.crm.customerCard.addresses.dragTooltip")}><Box sx={{
          width: 18, height: 36, display: "grid", placeItems: "center", flexShrink: 0, cursor: "grab", borderRadius: .75,
          color: draggedKey === fieldKey ? "primary.main" : ui.subtle, "&:hover": { bgcolor: "#eef1f4", color: ui.text }, "&:active": { cursor: "grabbing", bgcolor: "#e4e7ec" }
        }}>
          <DragIndicatorRoundedIcon sx={{ fontSize: 16 }} /></Box></Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>{renderValueField(fieldKey, definition)}</Box>
        <Stack spacing={.15} sx={{ width: 52, flexShrink: 0, alignItems: "flex-end" }}>
          <AddressFieldActionControl value={setting.type} onEdit={() => setEditingFieldKey(fieldKey)} />
          {settingChanged && <Stack direction="row" spacing={.2} alignItems="center" justifyContent="flex-end">
            <Tooltip title={t("admin.crm.customerCard.addresses.fieldSettingChangedTooltip")}><WarningAmberRoundedIcon sx={{ color: "#b54708", fontSize: 10.5 }} /></Tooltip>
            <Tooltip title={t("admin.crm.customerCard.addresses.resetFieldSettingTooltip")}><IconButton size="small" onClick={() => resetSetting(fieldKey, definition, masterSetting)} sx={{ width: 18, height: 18, color: ui.muted }}><RestartAltRoundedIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
          </Stack>}
        </Stack>
      </Stack>;
    })}

    <AddressFieldEditorDialog
      open={Boolean(editingFieldKey)}
      fieldLabel={
        editingFieldKey
          ? displayLabel(
            editingFieldKey,
            ADDRESS_LINE_DEFINITIONS[editingFieldKey]
          )
          : ""
      }
      fieldType={
        editingFieldKey
          ? addressSettings?.[editingFieldKey]?.type || "OPTIONAL"
          : "OPTIONAL"
      }
      onClose={() => setEditingFieldKey(null)}
      onSave={({ label, type }) => {
        if (!editingFieldKey) return;

        setAddressFieldSetting(
          addressType,
          editingFieldKey,
          "label",
          label
        );

        setAddressFieldSetting(
          addressType,
          editingFieldKey,
          "type",
          type
        );

        setEditingFieldKey(null);
      }}
    />
  </Stack>;
};

export default DraggableAddressLines;
